import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarMenuDTO } from '../../types/sidebar';
import SvgIcon from '../SvgIcon/SvgIcon';
import SidebarMenuLinkV2 from './SidebarMenuLinkV2';

interface Props {
  menu: SidebarMenuDTO;
  depth: number;
}

const SidebarMenuNodeV2: React.FC<Props> = ({ menu, depth }) => {
  const { pathname } = useLocation();

  const children = Array.isArray(menu.children) ? menu.children : [];
  const isGroup = menu.menuType === 'GROUP' || children.length > 0;

  const isRouteActive = (route?: string | null) => {
    if (!route) return false;

    return pathname === route || pathname.startsWith(`${route}/`);
  };

  const hasActiveChild = (items: SidebarMenuDTO[]): boolean => {
    return items.some((item) => {
      const itemActive = isRouteActive(item.route);
      const itemChildren = Array.isArray(item.children) ? item.children : [];

      return itemActive || hasActiveChild(itemChildren);
    });
  };

  const isActiveGroup = isRouteActive(menu.route) || hasActiveChild(children);

  const [expanded, setExpanded] = useState(
    menu.expandedByDefault === true || isActiveGroup
  );

  useEffect(() => {
    if (isActiveGroup) {
      setExpanded(true);
    }
  }, [isActiveGroup]);

  if (!isGroup) {
    return <SidebarMenuLinkV2 menu={menu} />;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className={`
          flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition

          ${
            isActiveGroup
              ? 'bg-graydark/80 text-white'
              : 'text-gray-300 hover:bg-graydark/60 hover:text-white'
          }
        `}
      >
        {menu.icon && (
          <SvgIcon
            name={menu.icon as any}
            className="h-4 w-4 shrink-0"
          />
        )}

        <span className="min-w-0 flex-1 truncate">{menu.name}</span>

        <SvgIcon
          name="chevron-down-mini"
          className={`h-4 w-4 shrink-0 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {expanded && children.length > 0 && depth < 3 && (
        <div className="ml-3 mt-1 space-y-1 border-l border-gray-700 pl-2">
          {children.map((child) => (
            <SidebarMenuNodeV2
              key={child.id ?? child.code}
              menu={child}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SidebarMenuNodeV2;